/**
 * Full-screen alarm when a recording ends for any reason OTHER than the
 * operator pressing stop (abort/discard, completeness stop-keep, obs-gen
 * death). The recording service latches `unacked_alarm` into its heartbeat
 * and keeps it raised — across further takes — until the operator presses
 * Acknowledge here, which clears it via /operator/session/alarm/ack.
 *
 * Behavior is governed by persistent settings (admin Settings page, served
 * by /operator/session/alarm/config): `enabled` (default on) suppresses the
 * modal entirely when off; `video_url` picks the YouTube link the modal
 * embeds as its sound/visual. When YouTube is unreachable (LAN without
 * internet — navigator.onLine can't see that, so we probe a thumbnail), a
 * WebAudio two-tone beep loops instead. Browsers may hold audio until the
 * first user gesture; the modal itself is always visible.
 */

import { useEffect, useRef, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { useRobotStore } from '@/stores/useRobotStore';
import { apiGet, apiPost } from '@/lib/api';

type UnackedAlarm = { reason: string; at_ms: number };
type AlarmConfig = { enabled: boolean; video_url: string };

/** youtu.be/<id>?t=43 and youtube.com/watch?v=<id>&t=43s forms. */
export function parseYoutubeUrl(url: string): { id: string; start: number } | null {
  try {
    const u = new URL(url);
    let id = '';
    if (u.hostname.endsWith('youtu.be')) id = u.pathname.slice(1).split('/')[0];
    else if (u.pathname.startsWith('/embed/')) id = u.pathname.split('/')[2] ?? '';
    else id = u.searchParams.get('v') ?? '';
    if (!/^[\w-]{6,20}$/.test(id)) return null;
    const t = u.searchParams.get('t') ?? u.searchParams.get('start') ?? '0';
    const start = parseInt(t.replace(/s$/, ''), 10);
    return { id, start: Number.isFinite(start) ? start : 0 };
  } catch {
    return null;
  }
}

function useAlarmConfig(active: boolean): AlarmConfig | null {
  const [config, setConfig] = useState<AlarmConfig | null>(null);
  useEffect(() => {
    if (!active) return;
    let cancelled = false;
    apiGet<AlarmConfig>('/operator/session/alarm/config')
      .then((c) => {
        if (!cancelled) setConfig(c);
      })
      // Config unreachable → alarm anyway (fail toward ringing, not silence).
      .catch(() => {
        if (!cancelled) setConfig({ enabled: true, video_url: '' });
      });
    return () => {
      cancelled = true;
    };
  }, [active]);
  return config;
}

/** True once YouTube is confirmed reachable, false once confirmed not (LAN
 * without internet is this rig's real failure mode — navigator.onLine can't
 * see it, so probe a lightweight thumbnail with a timeout). Null while
 * probing; the beep fallback runs unless the probe SUCCEEDS. */
function useYoutubeReachable(active: boolean, videoId: string | null): boolean | null {
  const [reachable, setReachable] = useState<boolean | null>(null);
  useEffect(() => {
    if (!active || !videoId) return;
    let done = false;
    const img = new Image();
    const timer = setTimeout(() => {
      if (!done) {
        done = true;
        setReachable(false);
      }
    }, 3000);
    img.onload = () => {
      if (!done) {
        done = true;
        clearTimeout(timer);
        setReachable(true);
      }
    };
    img.onerror = () => {
      if (!done) {
        done = true;
        clearTimeout(timer);
        setReachable(false);
      }
    };
    img.src = `https://i.ytimg.com/vi/${videoId}/default.jpg?_=${Date.now()}`;
    return () => {
      done = true;
      clearTimeout(timer);
    };
  }, [active, videoId]);
  return reachable;
}

/** Fallback when the video can't play: repeating two-tone beep. */
function useAlarmBeep(active: boolean) {
  const ctxRef = useRef<AudioContext | null>(null);
  useEffect(() => {
    if (!active) return;
    const ctx = ctxRef.current ?? new AudioContext();
    ctxRef.current = ctx;
    let stopped = false;
    const beep = (freq: number, at: number, dur: number) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'square';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.12, at);
      gain.gain.exponentialRampToValueAtTime(0.001, at + dur);
      osc.connect(gain).connect(ctx.destination);
      osc.start(at);
      osc.stop(at + dur);
    };
    const interval = setInterval(() => {
      if (stopped || ctx.state !== 'running') {
        void ctx.resume();
        return;
      }
      const t = ctx.currentTime;
      beep(880, t, 0.18);
      beep(660, t + 0.25, 0.18);
    }, 1200);
    // ctx.resume() from a timer is NOT a user gesture — if the page had no
    // user activation when the alarm latched (kiosk reload, pedal-driven
    // takes), the context stays suspended until a REAL interaction. One-shot
    // listeners resume it from the first pointer/key anywhere, so the sound
    // starts without the operator having to click the modal (which would
    // acknowledge before they ever heard it).
    const resume = () => void ctx.resume();
    window.addEventListener('pointerdown', resume);
    window.addEventListener('keydown', resume);
    return () => {
      stopped = true;
      clearInterval(interval);
      window.removeEventListener('pointerdown', resume);
      window.removeEventListener('keydown', resume);
    };
  }, [active]);
}

export function RecordingAlarmModal() {
  const heartbeats = useRobotStore((s) => s.heartbeats);
  const [acking, setAcking] = useState(false);
  const recordingHb = heartbeats.find((hb) => hb.heartbeat_id === 'recording_service');
  const alarm = (recordingHb?.payload as { unacked_alarm?: UnackedAlarm | null } | undefined)
    ?.unacked_alarm;

  const config = useAlarmConfig(Boolean(alarm));
  const enabled = config?.enabled !== false;
  const video = config ? parseYoutubeUrl(config.video_url) : null;
  const youtubeReachable = useYoutubeReachable(Boolean(alarm) && enabled, video?.id ?? null);
  const showVideo = Boolean(video) && youtubeReachable === true;
  useAlarmBeep(Boolean(alarm) && enabled && config !== null && !showVideo);

  if (!alarm || !enabled) return null;

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-red-950/70 backdrop-blur-sm">
      <div className="relative bg-surface border-2 border-red-500 rounded-2xl shadow-2xl p-10 w-[42rem] flex flex-col gap-6 items-center text-center">
        {showVideo && video && (
          <iframe
            className="w-full aspect-video rounded-xl border border-red-500/40"
            src={`https://www.youtube.com/embed/${video.id}?autoplay=1&start=${video.start}&loop=1&playlist=${video.id}`}
            title="Recording alarm"
            allow="autoplay; encrypted-media"
          />
        )}
        <div className="flex flex-col gap-3 items-center">
          <AlertTriangle size={40} className="text-red-500 animate-pulse" />
          <p className="text-2xl font-semibold text-red-500 leading-snug tracking-tight">
            Recording stopped unexpectedly
          </p>
          <p className="text-base text-foreground leading-relaxed max-w-lg break-words">
            {alarm.reason}
          </p>
          <p className="text-sm text-foreground/60">{new Date(alarm.at_ms).toLocaleTimeString()}</p>
        </div>
        <button
          disabled={acking}
          onClick={() => {
            setAcking(true);
            apiPost('/operator/session/alarm/ack', { at_ms: alarm.at_ms })
              .catch(() => undefined)
              .finally(() => setAcking(false));
          }}
          className="rounded-lg bg-red-500 px-8 py-3 text-base font-semibold text-white hover:bg-red-600 transition disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {acking ? 'Acknowledging…' : 'Acknowledge'}
        </button>
      </div>
    </div>
  );
}
